import qz from 'qz-tray';
import { logQzTrayConnection, logPrinterStatus } from './src/utils/frontendLogger';

const CONNECT_TIMEOUT_MS = 15000;

let _connectPromise = null;
let _status = 'disconnected';
let _certsRegistered = false;
let _printerCallbacksArmed = false;
let _currentAlertBox = null;

export const getStatus = () => _status;

export const setCurrentAlertBox = (alertBox) => {
  _currentAlertBox = (typeof alertBox === 'function') ? alertBox : null;
};

const withTimeout = (promise, ms, label) => {
  let timer;
  const t = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms);
  });
  return Promise.race([promise, t]).finally(() => clearTimeout(timer));
};

const setupCerts = () => {
  if (_certsRegistered) return;
  qz.security.setCertificatePromise((resolve, reject) => {
    fetch('https://admin.groovepackerapi.com/print/qz_certificate').then((data) => {
      data.ok ? resolve(data.text()) : reject(data.text());
    });
  });

  qz.security.setSignatureAlgorithm('SHA512');
  qz.security.setSignaturePromise((toSign) => {
    return (resolve, reject) => {
      fetch(`https://admin.groovepackerapi.com/print/qz_sign?request=${toSign}`, {
        cache: 'no-store',
        headers: { 'Content-Type': 'text/plain' },
        method: 'POST'
      }).then((data) => {
        data.ok ? resolve(data.text()) : reject(data.text());
      });
    };
  });

  _certsRegistered = true;
};

const armPrinterCallbacks = () => {
  if (_printerCallbacksArmed) return;
  qz.printers.setPrinterCallbacks((evt) => {
    if (evt && (evt.severity === 'ERROR' || evt.severity === 'FATAL')) {
      const msg = `🖨️ Printer Error: ${evt.printerName || 'Unknown Printer'}\n${evt.message}`;
      if (_currentAlertBox) {
        _currentAlertBox(msg, null, true);
      }
    }
    logPrinterStatus(evt);
  });
  qz.printers.startListening();
  _printerCallbacksArmed = true;
};

const CONNECTION_LOG_INFO = {
  host: 'localhost',
  port: 8181,
  secure: true,
  address: 'wss://localhost:8181'
};

export const initConnection = () => {
  setupCerts(); // synchronous, before any connect
  qz.websocket.setClosedCallbacks([() => {
    _status = 'disconnected';
    _printerCallbacksArmed = false;
  }]);
  qz.websocket.setErrorCallbacks([(err) => {
    console.error('[QZ] WebSocket error:', err);
  }]);
};

export const ensureConnected = () => {
  if (qz.websocket.isActive()) { _status = 'connected'; return Promise.resolve(); }
  if (_connectPromise) return _connectPromise;

  _status = 'connecting';
  const p = (async () => {
    try {
      await withTimeout((async () => {
        try {
          await qz.websocket.connect();
        } catch (firstErr) {
          await qz.websocket.connect({ retries: 2, delay: 1 });
        }
      })(), CONNECT_TIMEOUT_MS, 'QZ connect');

      _status = 'connected';
      try { armPrinterCallbacks(); } catch (e) { console.error('[QZ] failed to arm printer callbacks:', e); }
      logQzTrayConnection(CONNECTION_LOG_INFO, true);
    } catch (err) {
      _status = 'error';
      logQzTrayConnection(CONNECTION_LOG_INFO, false, err);
      try { if (qz.websocket.isActive()) qz.websocket.disconnect(); } catch (_) { /* noop */ }
      throw err;
    } finally {
      if (_connectPromise === p) _connectPromise = null;
    }
  })();

  _connectPromise = p;
  return p;
};

export const __resetForTests = () => {
  _connectPromise = null;
  _status = 'disconnected';
  _certsRegistered = false;
  _printerCallbacksArmed = false;
  _currentAlertBox = null;
};

export const __withTimeout = withTimeout;
